import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SideToSideCatcherGame.css';

// Game constants
const STAGE_W = 640;
const STAGE_H = 360;

const PLAYER_SIZE = 110;
const PLAYER_Y = STAGE_H - 90;
const PLAYER_SPEED = 280; // px/sec
const PLAYER_CATCH_W = PLAYER_SIZE * 0.65;
const PLAYER_CATCH_H = PLAYER_SIZE * 0.45;

const GAME_DURATION = 60; // 60 seconds

const ITEM_SIZES = {
  flashcard: { w: 72, h: 72, score: 10 },
  star: { w: 64, h: 64, score: 25 },
  redx: { w: 64, h: 64, score: -15 },
  crumpled: { w: 72, h: 72, score: -10 },
};

const ITEM_WEIGHTS = {
  flashcard: 0.5,  // Common
  star: 0.15,      // Rare
  redx: 0.2,       // Uncommon penalty
  crumpled: 0.15,  // Uncommon penalty
};

// Difficulty settings
const SPAWN_INTERVAL_START = 650;  // ms
const SPAWN_INTERVAL_MIN = 350;    // ms (cap)
const FALL_SPEED_START = 140;      // px/sec
const FALL_SPEED_MAX = 260;        // px/sec (cap)
const DIFFICULTY_RAMP_DURATION = 100; // seconds until plateau

// Milestone settings
const MILESTONE_INTERVAL = 60;     // seconds
const MILESTONE_BONUS = 100;

// Star burst settings
const STAR_BURST_INTERVAL = 90;    // seconds between bursts
const STAR_BURST_DURATION = 8;     // seconds

/**
 * Side-to-Side Catcher Mini-Game Component
 * 
 * Props:
 * - isActive: boolean - controls game loop (pause when false)
 * - onScoreChange: (score) => void - called when score updates
 * - onGameEnd: (finalScore) => void - called when game ends
 * - currentScore: number - current score from parent
 * - onExit: () => void - called when exit button is clicked
 * - showFinalScore: boolean - whether to show final score
 * - finalScoreData: object - final score data
 */
export default function SideToSideCatcherGame({ 
  isActive = true, 
  onScoreChange, 
  onGameEnd,
  currentScore = 0,
  onExit,
  showFinalScore = false,
  finalScoreData = null,
  isCompact = false,
  contentReady = false,
  contentTypeName = '',
  onViewContent
}) {
  // Adjust constants for compact mode
  const EFFECTIVE_PLAYER_Y = isCompact ? STAGE_H - 110 : PLAYER_Y; // Higher position in compact
  const EFFECTIVE_PLAYER_SPEED = isCompact ? 480 : PLAYER_SPEED; // Much faster in compact
  const EFFECTIVE_PLAYER_CATCH_H = isCompact ? PLAYER_SIZE * 0.45 : PLAYER_CATCH_H; // Smaller catch zone in compact
  const EFFECTIVE_CATCH_OFFSET_Y = isCompact ? 75 : 0; // Very large offset for visual overlap
  
  // Game state
  const [gameState, setGameState] = useState('instructions'); // 'instructions' | 'countdown' | 'playing' | 'gameover'
  const [countdownNumber, setCountdownNumber] = useState(3);
  const [score, setScore] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(GAME_DURATION);
  const [highScore, setHighScore] = useState(0);
  const [playerX, setPlayerX] = useState(STAGE_W / 2 - PLAYER_SIZE / 2);
  const [playerDirection, setPlayerDirection] = useState(0); // -1 left, 0 idle, 1 right
  const [objects, setObjects] = useState([]);
  const [popups, setPopups] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  
  // Refs for animation loop
  const rafIdRef = useRef(null);
  const lastFrameRef = useRef(performance.now());
  const spawnIntervalRef = useRef(null);
  const gameStartTimeRef = useRef(performance.now());
  const lastMilestoneRef = useRef(0);
  // eslint-disable-next-line no-unused-vars
  const starBurstModeRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const starBurstStartRef = useRef(0);
  const isPausedRef = useRef(false);
  const stageRef = useRef(null);
  const stageWidthRef = useRef(640); // Track actual stage width for speed scaling
  
  // Input state
  const keysRef = useRef({ left: false, right: false });
  const touchDirectionRef = useRef(0);

  // Notify parent of score changes
  useEffect(() => {
    if (onScoreChange) {
      onScoreChange(score);
    }
  }, [score, onScoreChange]);

  // Load high score on mount
  useEffect(() => {
    const saved = localStorage.getItem('sideToSideHighScore');
    if (saved) {
      setHighScore(parseInt(saved, 10));
    }
  }, []);

  // Track stage width for speed scaling
  useEffect(() => {
    const updateStageWidth = () => {
      if (stageRef.current) {
        stageWidthRef.current = stageRef.current.offsetWidth;
      }
    };
    
    // Update immediately and on resize
    const timeoutId = setTimeout(updateStageWidth, 0);
    window.addEventListener('resize', updateStageWidth);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateStageWidth);
    };
  }, [gameState]);

  // Update high score if current score exceeds it
  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('sideToSideHighScore', score.toString());
    }
  }, [score, highScore]);

  // Game timer countdown
  useEffect(() => {
    if (gameState !== 'playing') return;

    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          setGameState('gameover');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [gameState]);


  // Handle start game button
  const handleStartGame = useCallback(() => {
    setGameState('countdown');
    setCountdownNumber(3);
    setTimeRemaining(GAME_DURATION);
    setScore(0);
    setObjects([]);
    setPlayerX(STAGE_W / 2 - PLAYER_SIZE / 2);
    
    // Countdown sequence
    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownNumber(count);
      } else {
        clearInterval(countdownInterval);
        setGameState('playing');
      }
    }, 1000);
  }, []);

  // Handle play again
  const handlePlayAgain = useCallback(() => {
    handleStartGame();
  }, [handleStartGame]);

  // Handle back to games
  // eslint-disable-next-line no-unused-vars
  const handleBackToGames = useCallback(() => {
    setGameState('instructions');
    setScore(0);
    setObjects([]);
    setPlayerX(STAGE_W / 2 - PLAYER_SIZE / 2);
    setTimeRemaining(GAME_DURATION);
  }, []);

  // Calculate current difficulty based on game time
  const getCurrentDifficulty = useCallback(() => {
    if (isPausedRef.current) return null;
    
    const elapsed = (performance.now() - gameStartTimeRef.current) / 1000;
    const progress = Math.min(elapsed / DIFFICULTY_RAMP_DURATION, 1);
    
    const spawnInterval = SPAWN_INTERVAL_START - (SPAWN_INTERVAL_START - SPAWN_INTERVAL_MIN) * progress;
    const fallSpeed = FALL_SPEED_START + (FALL_SPEED_MAX - FALL_SPEED_START) * progress;
    
    // Check for star burst mode
    const timeSinceLastBurst = elapsed % STAR_BURST_INTERVAL;
    const inStarBurst = timeSinceLastBurst < STAR_BURST_DURATION;
    
    return {
      spawnInterval,
      fallSpeed,
      inStarBurst,
      elapsed,
    };
  }, []);

  // Spawn a new falling object
  const spawnObject = useCallback(() => {
    const difficulty = getCurrentDifficulty();
    if (!difficulty) return;
    
    // Determine item type based on weights (or star burst)
    let itemType;
    if (difficulty.inStarBurst && Math.random() < 0.7) {
      itemType = 'star'; // 70% stars during burst
    } else {
      const rand = Math.random();
      let cumulative = 0;
      for (const [type, weight] of Object.entries(ITEM_WEIGHTS)) {
        cumulative += weight;
        if (rand < cumulative) {
          itemType = type;
          break;
        }
      }
    }
    
    const itemData = ITEM_SIZES[itemType];
    const newObject = {
      id: Date.now() + Math.random(),
      type: itemType,
      x: Math.random() * (STAGE_W - itemData.w),
      y: -itemData.h,
      speed: difficulty.fallSpeed,
    };
    
    setObjects(prev => [...prev, newObject]);
  }, [getCurrentDifficulty]);

  // Check AABB collision
  const checkCollision = useCallback((objX, objY, objW, objH) => {
    const catchX = playerX + (PLAYER_SIZE - PLAYER_CATCH_W) / 2;
    const catchY = EFFECTIVE_PLAYER_Y + (PLAYER_SIZE - EFFECTIVE_PLAYER_CATCH_H) / 2 + EFFECTIVE_CATCH_OFFSET_Y;
    
    return (
      objX < catchX + PLAYER_CATCH_W &&
      objX + objW > catchX &&
      objY < catchY + EFFECTIVE_PLAYER_CATCH_H &&
      objY + objH > catchY
    );
  }, [playerX, EFFECTIVE_PLAYER_Y, EFFECTIVE_PLAYER_CATCH_H, EFFECTIVE_CATCH_OFFSET_Y]);

  // Add floating popup
  const addPopup = useCallback((text, x, y) => {
    const popup = {
      id: Date.now() + Math.random(),
      text,
      x,
      y,
      createdAt: performance.now(),
    };
    setPopups(prev => [...prev, popup]);
    
    // Auto-remove after 800ms
    setTimeout(() => {
      setPopups(prev => prev.filter(p => p.id !== popup.id));
    }, 800);
  }, []);

  // Game loop
  const gameLoop = useCallback(() => {
    if (!isActive || isPausedRef.current || gameState !== 'playing') {
      return;
    }
    
    const now = performance.now();
    let deltaTime = (now - lastFrameRef.current) / 1000;
    lastFrameRef.current = now;
    
    // Cap deltaTime to prevent large jumps (e.g., when tab becomes visible again)
    deltaTime = Math.min(deltaTime, 0.1);
    
    const difficulty = getCurrentDifficulty();
    if (!difficulty) return;
    
    // Update player position
    const direction = touchDirectionRef.current || (keysRef.current.left ? -1 : keysRef.current.right ? 1 : 0);
    setPlayerDirection(direction);
    
    if (direction !== 0) {
      // Scale speed based on actual stage width (slower when narrower)
      const speedMultiplier = stageWidthRef.current / STAGE_W;
      setPlayerX(prev => {
        const newX = prev + direction * EFFECTIVE_PLAYER_SPEED * speedMultiplier * deltaTime;
        return Math.max(0, Math.min(STAGE_W - PLAYER_SIZE, newX));
      });
    }
    
    // Update falling objects
    setObjects(prev => {
      const updated = [];
      let scoreChange = 0;
      const popupsToAdd = [];
      
      // Scale falling speed based on actual stage width (slower when narrower)
      const speedMultiplier = stageWidthRef.current / STAGE_W;
      
      for (const obj of prev) {
        const itemData = ITEM_SIZES[obj.type];
        const newY = obj.y + obj.speed * speedMultiplier * deltaTime;
        
        // Check collision
        if (checkCollision(obj.x, newY, itemData.w, itemData.h)) {
          scoreChange += itemData.score;
          popupsToAdd.push({
            text: itemData.score > 0 ? `+${itemData.score}` : `${itemData.score}`,
            x: obj.x + itemData.w / 2,
            y: newY,
          });
          continue; // Remove object
        }
        
        // Remove if off screen
        if (newY > STAGE_H) {
          continue;
        }
        
        updated.push({ ...obj, y: newY });
      }
      
      // Apply score changes
      if (scoreChange !== 0) {
        setScore(prevScore => Math.max(0, prevScore + scoreChange));
      }
      
      // Add popups
      popupsToAdd.forEach(p => addPopup(p.text, p.x, p.y));
      
      return updated;
    });
    
    // Check for milestone bonus
    const currentMilestone = Math.floor(difficulty.elapsed / MILESTONE_INTERVAL);
    if (currentMilestone > lastMilestoneRef.current) {
      lastMilestoneRef.current = currentMilestone;
      setScore(prevScore => prevScore + MILESTONE_BONUS);
      addPopup(`+${MILESTONE_BONUS} Milestone!`, STAGE_W / 2, STAGE_H / 2);
    }
    
    rafIdRef.current = requestAnimationFrame(gameLoop);
  }, [isActive, checkCollision, addPopup, getCurrentDifficulty, gameState, EFFECTIVE_PLAYER_SPEED]);

  // Pause/resume handlers
  const pauseGame = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
    
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    if (spawnIntervalRef.current) {
      clearInterval(spawnIntervalRef.current);
      spawnIntervalRef.current = null;
    }
  }, []);

  const resumeGame = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
    lastFrameRef.current = performance.now();
    
    // Restart spawn interval
    const difficulty = getCurrentDifficulty();
    if (difficulty && !spawnIntervalRef.current) {
      spawnIntervalRef.current = setInterval(() => {
        const currentDifficulty = getCurrentDifficulty();
        if (currentDifficulty) {
          spawnObject();
        }
      }, difficulty.spawnInterval);
    }
    
    // Restart animation loop
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(gameLoop);
    }
  }, [gameLoop, spawnObject, getCurrentDifficulty]);

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        keysRef.current.left = true;
        e.preventDefault();
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        keysRef.current.right = true;
        e.preventDefault();
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        keysRef.current.left = false;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        keysRef.current.right = false;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Tab visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        pauseGame();
      } else if (isActive) {
        resumeGame();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, pauseGame, resumeGame]);

  // Start/stop game loop based on isActive prop and game state
  useEffect(() => {
    if (isActive && !isPausedRef.current && gameState === 'playing') {
      // Reset game start time on first activation
      if (!rafIdRef.current) {
        gameStartTimeRef.current = performance.now();
        lastFrameRef.current = performance.now();
      }
      
      // Start spawn interval
      const difficulty = getCurrentDifficulty() || { spawnInterval: SPAWN_INTERVAL_START };
      spawnIntervalRef.current = setInterval(() => {
        spawnObject();
      }, difficulty.spawnInterval);
      
      // Start animation loop
      rafIdRef.current = requestAnimationFrame(gameLoop);
    } else {
      // Stop everything
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
        spawnIntervalRef.current = null;
      }
    }
    
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
      }
    };
  }, [isActive, gameLoop, spawnObject, getCurrentDifficulty, gameState]);

  // Touch/pointer handlers for mobile
  const handlePointerDown = (e, side) => {
    touchDirectionRef.current = side === 'left' ? -1 : 1;
    e.preventDefault();
  };

  const handlePointerUp = () => {
    touchDirectionRef.current = 0;
  };

  // Get player sprite based on direction
  const getPlayerSprite = () => {
    if (playerDirection < 0) return '/scout_images/games/sidetosidecatcher/scout_move_left.png';
    if (playerDirection > 0) return '/scout_images/games/sidetosidecatcher/scout_move_right.png';
    return '/scout_images/games/sidetosidecatcher/scout_center.png';
  };

  // Get falling object sprite
  const getObjectSprite = (type) => {
    const sprites = {
      flashcard: '/scout_images/games/sidetosidecatcher/falling_flashcard.png',
      star: '/scout_images/games/sidetosidecatcher/falling_star.png',
      redx: '/scout_images/games/sidetosidecatcher/falling_redx.png',
      crumpled: '/scout_images/games/sidetosidecatcher/falling_crumpled_paper.png',
    };
    return sprites[type];
  };

  return (
    <div className={`side-to-side-catcher ${isCompact ? 'side-to-side-catcher--compact' : ''}`}>
      {/* Exit button overlay - always visible */}
      <button className="game-exit-overlay" onClick={onExit}>
        ✕
      </button>
      
      {/* Score overlay - only visible when playing */}
      {gameState === 'playing' && (
        <div className="game-score-overlay">
          <div className="score-line">Score: {currentScore}</div>
          <div className="timer-line">Time: {timeRemaining}s</div>
          <div className="highscore-line">High Score: {highScore}</div>
        </div>
      )}
      
      <div 
        ref={stageRef}
        className="game-stage"
        style={{
          backgroundImage: 'url(/scout_images/games/sidetosidecatcher/sidetoside_backgorund.png)',
        }}
      >
        {/* Touch zones for mobile */}
        <div 
          className="touch-zone touch-zone-left"
          onPointerDown={(e) => handlePointerDown(e, 'left')}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        <div 
          className="touch-zone touch-zone-right"
          onPointerDown={(e) => handlePointerDown(e, 'right')}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        
        {/* Player (Scout) */}
        <img
          className="player"
          src={getPlayerSprite()}
          alt="Scout"
          style={{
            left: `${(playerX / STAGE_W) * 100}%`,
            bottom: `${((STAGE_H - EFFECTIVE_PLAYER_Y - PLAYER_SIZE) / STAGE_H) * 100}%`,
            width: `${PLAYER_SIZE}px`,
            height: `${PLAYER_SIZE}px`,
          }}
        />
        
        {/* Falling objects */}
        {objects.map(obj => {
          const itemData = ITEM_SIZES[obj.type];
          return (
            <img
              key={obj.id}
              className={`falling-object falling-${obj.type}`}
              src={getObjectSprite(obj.type)}
              alt={obj.type}
              style={{
                left: `${(obj.x / STAGE_W) * 100}%`,
                top: `${(obj.y / STAGE_H) * 100}%`,
                width: `${itemData.w}px`,
                height: `${itemData.h}px`,
              }}
            />
          );
        })}
        
        {/* Score popups */}
        {popups.map(popup => (
          <div
            key={popup.id}
            className="score-popup"
            style={{
              left: `${(popup.x / STAGE_W) * 100}%`,
              top: `${(popup.y / STAGE_H) * 100}%`,
            }}
          >
            {popup.text}
          </div>
        ))}
        
        {/* Paused indicator */}
        {isPaused && (
          <div className="paused-indicator">
            Paused
          </div>
        )}
        
        {/* Instructions screen */}
        {gameState === 'instructions' && (
          <div className="game-instructions-overlay">
            <div className="instructions-content">
              <h2>Side-to-Side Catcher</h2>
              <div className="instructions-rules">
                <p><strong>How to Play:</strong></p>
                <p className="timer-notice">You have <strong>60 seconds</strong> to score as many points as possible!</p>
                <ul>
                  <li>Move Scout left and right to catch falling items</li>
                  <li>🎴 Flashcards: +10 points</li>
                  <li>⭐ Stars: +25 points</li>
                  <li>❌ Red X: -15 points</li>
                  <li>📄 Crumpled Paper: -10 points</li>
                </ul>
                <p className="controls-hint">
                  <strong>Controls:</strong> Arrow keys or A/D
                </p>
              </div>
              <button className="start-game-btn" onClick={handleStartGame}>
                Start Game
              </button>
            </div>
          </div>
        )}
        
        {/* Countdown circle */}
        {gameState === 'countdown' && (
          <div className="countdown-overlay">
            <div className="countdown-circle">
              <span className="countdown-number">{countdownNumber}</span>
            </div>
          </div>
        )}

        {/* Game Over screen */}
        {gameState === 'gameover' && (
          <div className="game-over-overlay">
            <div className="game-over-content">
              <h2 className="game-over-title">
                {score >= highScore && score > 0 ? '🏆 New High Score!' : 'Game Over!'}
              </h2>
              <p className="game-over-message">
                {score >= highScore && score > 0 ? 'Great job!' : 'Good job!'}
              </p>
              <div className="game-over-stats">
                <div className="stat-item">
                  <span className="stat-label">Your Score</span>
                  <span className="stat-value">{score}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">High Score</span>
                  <span className="stat-value">{highScore}</span>
                </div>
              </div>
              <div className="game-over-buttons">
                <button className="game-over-btn play-again-btn" onClick={handlePlayAgain}>
                  Play Again
                </button>
              </div>
              {contentReady && (
                <div className="game-over-ready-banner">
                  <span>✅ Your {contentTypeName} is ready!</span>
                  <button className="game-over-btn view-content-btn" onClick={onViewContent}>
                    View {contentTypeName}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
